import React, { useState } from 'react';
import FloatingShapes from "../common/FloatingShapes";
import {
  AcademicCapIcon,
  ScaleIcon,
  TrophyIcon,
  PaintBrushIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const programs = [
  {
    title: "Үндсэн хөтөлбөр",
    description:
      "Хүүхдийн хөгжил, багаар ажиллах чадвар, амьдрах ухааны үндсэн дадлыг олгоно.",
    icon: AcademicCapIcon,
    gradient: "from-primary-500 to-primary-400",
    accent: "from-primary-500 to-primary-400",
    image: "/daily-adventure.jpg",
  },
  {
    title: "Хууль эрх зүйн хөтөлбөр",
    description:
      "Хүүхдийн эрх, хууль эрх зүйн мэдлэг олгох, иргэний боловсрол дээшлүүлэх.",
    icon: ScaleIcon,
    gradient: "from-secondary-500 to-secondary-400",
    accent: "from-secondary-500 to-secondary-400",
    image: "/daily-law.jpg",
  },
  {
    title: "Спортын хөтөлбөр",
    description:
      "Биеийн тамирын дасгал, багийн спорт, эрүүл мэндийн зөв дадал хэвшил.",
    icon: TrophyIcon,
    gradient: "from-warm-500 to-warm-400",
    accent: "from-warm-500 to-warm-400",
    image: "/daily-sport.jpg",
  },
  {
    title: "Урлагийн хөтөлбөр",
    description:
      "Бүжиг, дуу хөгжим, зураг, гар урлалын бүтээлч үйл ажиллагаанууд.",
    icon: PaintBrushIcon,
    gradient: "from-accent-500 to-accent-400",
    accent: "from-accent-500 to-accent-400",
    image: "/daily-art.jpg",
  },
  {
    title: "Байгаль & Хүүхэд хөтөлбөр",
    description:
      "Байгаль орчныг таних, хамгаалах, экологийн боловсрол олгох аялал.",
    icon: GlobeAltIcon,
    gradient: "from-primary-500 to-secondary-400",
    accent: "from-primary-500 to-secondary-400",
    image: "/daily-nature.jpg",
  },
];

export default function DailyTrip() {
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);

  return (
    <>
      {/* ───────────────── Hero Section (Dark) ───────────────── */}
      <section className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute -bottom-20 right-1/3 w-[400px] h-[400px] rounded-full bg-primary-400/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative min-h-[60vh] flex items-center pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
            {/* Badge */}
            <div className="flex justify-center mb-10 animate-fade-in">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-primary-300 backdrop-blur-sm">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
                Бүтээгдэхүүн, үйлчилгээ
              </div>
            </div>

            {/* Gradient heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 animate-fade-in">
              <span className="bg-gradient-to-r from-primary-400 via-secondary-400 to-primary-300 bg-clip-text text-transparent">
                Өдрийн аялалын хөтөлбөр
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto animate-fade-in">
              АНГИ, ХАМТ ОЛНЫ ӨДРИЙН АЯЛАЛД УРЬЖ БАЙНА
            </p>
          </div>
        </div>
      </section>

      {/* ───────────────── Intro Section (White) ───────────────── */}
      <section className="relative bg-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[350px] rounded-full bg-secondary-100/50 blur-[80px]" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-slide-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-700 ring-1 ring-primary-200/50 mb-6">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
            Өдрийн аялал
          </div>

          <p className="text-lg sm:text-xl leading-8 text-neutral-600 mb-4">
            Манай цогцолбор анги, хамт олны захиалгыг зуны улирлаас бусад улиралд тогтмол авч байна.
          </p>
          <p className="text-lg sm:text-xl leading-8 text-neutral-700 font-semibold">
            Та бүхэн дараах хөтөлбөрүүдээс сонгох боломжтой:
          </p>
        </div>
      </section>

      {/* ───────────────── Program Cards (Dark) ───────────────── */}
      <section className="relative overflow-hidden bg-primary-800">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-warm-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="alt" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14 animate-fade-in">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Хөтөлбөрүүд
              </h2>
              <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" />
            </div>

            {/* Overview image */}
            <div className="mb-12 rounded-2xl overflow-hidden border border-white/10 shadow-2xl cursor-pointer" onClick={() => setLightboxImg('/daily-overview.png')}>
              <img src="/daily-overview.png" alt="Хөтөлбөрүүд" className="w-full h-auto object-cover hover:scale-[1.02] transition-transform duration-500" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {programs.map((program, index) => (
                <div
                  key={program.title}
                  className="relative bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/[0.08] transform hover:-translate-y-2 transition-all duration-300 group animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`absolute top-0 left-8 right-8 h-px bg-gradient-to-r ${program.accent} opacity-0 group-hover:opacity-100 transition-opacity z-10`} />

                  {/* Program poster image */}
                  <div className="relative overflow-hidden cursor-pointer" onClick={() => setLightboxImg(program.image)}>
                    <img
                      src={program.image}
                      alt={program.title}
                      className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${program.gradient} shadow-md flex-shrink-0`}>
                          <program.icon className="h-5 w-5 text-white" aria-hidden="true" />
                        </div>
                        <h3 className="text-lg font-bold text-white drop-shadow-lg">
                          {program.title}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-sm leading-relaxed text-slate-400">
                      {program.description}
                    </p>
                  </div>

                  <div className={`h-1 bg-gradient-to-r ${program.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Price Section (White) ───────────────── */}
      <section className="relative bg-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[350px] rounded-full bg-secondary-100/50 blur-[80px]" />
        </div>

        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 animate-slide-up">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">
              Үнийн мэдээлэл
            </h2>
            <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" />
          </div>

          {/* Highlighted price card */}
          <div className="relative bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200/50 rounded-2xl p-8 sm:p-12 text-center shadow-lg">
            <div className="absolute -inset-px bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 rounded-2xl opacity-10" />

            <div className="relative">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 shadow-md mb-6">
                <CurrencyDollarIcon className="h-8 w-8 text-white" aria-hidden="true" />
              </div>

              <p className="text-sm font-medium text-primary-600 uppercase tracking-wider mb-3">
                Үнэ
              </p>
              <p className="text-4xl sm:text-5xl font-extrabold text-neutral-900 mb-2">
                61,500₮
              </p>
              <p className="text-base text-neutral-500 mb-6">
                /Унаа, хоол, хөтөлбөр/
              </p>
              <p className="text-sm text-neutral-500 mb-8">
                (Бүх хөтөлбөр ижил үнэтэй)
              </p>

              <div className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-5 py-2.5 text-primary-700 font-medium">
                <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                Суралцаж, хөгжиж, дурсамж бүтээх гайхалтай боломж!
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Contact Section (Dark) ───────────────── */}
      <section className="relative overflow-hidden bg-primary-800">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="sparse" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Захиалга өгөх
            </h2>
            <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 mb-10" />

            <div className="inline-flex items-center gap-4 bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl px-8 py-6 sm:px-12 sm:py-8">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-secondary-500 to-secondary-600 shadow-md flex-shrink-0">
                <PhoneIcon className="h-7 w-7 text-white" aria-hidden="true" />
              </div>
              <div className="text-left">
                <p className="text-sm text-slate-400 mb-1">Захиалга авах</p>
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary-400 via-secondary-400 to-primary-300 bg-clip-text text-transparent">
                  75056010-3
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxImg(null)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <img
              src={lightboxImg}
              alt=""
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
}
